import { StandardEvents } from '@shopify/events';

/**
 * Keeps two cart attributes aligned with the cart:
 * - `helium_fill_pickup_only` while a helium fill line is present
 * - `balloon_bouquet_weight` while a bouquet-weight line is present
 * Each is cleared when its own line is removed. They do not share a flag.
 */

const FILL_ATTRIBUTE_KEY = 'helium_fill_pickup_only';
const WEIGHT_ATTRIBUTE_KEY = 'balloon_bouquet_weight';

const DEFAULT_WEIGHT_HANDLES = ['gewichtje-ballonboeket-536', 'test-weight'];

/** @type {number} */
let syncGeneration = 0;

/** @type {Promise<void>} */
let syncQueue = Promise.resolve();

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function attributeIsOn(value) {
  return value === true || value === 'true';
}

/**
 * @param {unknown} properties
 * @param {string} key
 * @returns {unknown}
 */
function propertyValue(properties, key) {
  if (!properties || typeof properties !== 'object') return undefined;

  if (Array.isArray(properties)) {
    const match = properties.find((entry) => {
      if (!entry || typeof entry !== 'object') return false;

      const record = /** @type {{ name?: string, key?: string }} */ (entry);
      return record.name === key || record.key === key;
    });

    if (!match || typeof match !== 'object') return undefined;

    return /** @type {{ value?: unknown }} */ (match).value;
  }

  return /** @type {Record<string, unknown>} */ (properties)[key];
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isTruthyFlag(value) {
  return value === true || value === 'true';
}

/**
 * @returns {Set<string>}
 */
function weightHandles() {
  const handles = new Set(DEFAULT_WEIGHT_HANDLES);
  const weightHandle = Theme.heliumPickup?.weightHandle;

  if (typeof weightHandle === 'string' && weightHandle.trim()) {
    handles.add(weightHandle.trim().toLowerCase());
  }

  return handles;
}

/**
 * @returns {Set<string>}
 */
function fillFallbackHandles() {
  const handles = new Set();
  const fillHandle = Theme.heliumPickup?.fillFallbackHandle;

  if (typeof fillHandle === 'string' && fillHandle.trim()) {
    handles.add(fillHandle.trim().toLowerCase());
  }

  return handles;
}

/**
 * Bouquet weight is linked to the balloon, but it is not a helium fill.
 * Older carts also stamped `_fill_pickup_only` on the weight line.
 * @param {Record<string, unknown>} item
 * @param {Set<string>} handles
 * @returns {boolean}
 */
function isBouquetWeightLine(item, handles) {
  if (propertyValue(item.properties, '_helium_addon_kind') === 'weight') return true;

  const handle = String(item.handle || '').toLowerCase();
  return Boolean(handle && handles.has(handle));
}

/**
 * Helium fill only. The parent balloon and the bouquet weight do not count.
 * @param {Record<string, unknown>} item
 * @param {Set<string>} weightHandleSet
 * @param {Set<string>} fillHandleSet
 * @returns {boolean}
 */
function lineRequiresHeliumPickup(item, weightHandleSet, fillHandleSet) {
  if (isBouquetWeightLine(item, weightHandleSet)) return false;

  const fillPickupOnly = propertyValue(item.properties, '_fill_pickup_only');
  if (isTruthyFlag(fillPickupOnly)) return true;

  if (propertyValue(item.properties, '_helium_fill_role') === 'addon') return true;

  const type = String(item.product_type || '').toLowerCase();
  const title = `${item.product_title || ''} ${item.title || ''}`.toLowerCase();
  const handle = String(item.handle || '').toLowerCase();

  if (type === 'vullen' && title.includes('helium')) return true;
  if (handle.startsWith('fill-hel-')) return true;
  if (handle && fillHandleSet.has(handle)) return true;

  return false;
}

/**
 * @param {Record<string, unknown>} cart
 * @returns {{ hasFill: boolean, hasWeight: boolean }}
 */
function cartAddonFlags(cart) {
  const items = Array.isArray(cart.items) ? cart.items : [];
  const weightHandleSet = weightHandles();
  const fillHandleSet = fillFallbackHandles();
  let hasFill = false;
  let hasWeight = false;

  for (const item of items) {
    if (!item || typeof item !== 'object') continue;

    const line = /** @type {Record<string, unknown>} */ (item);

    if (!hasWeight && isBouquetWeightLine(line, weightHandleSet)) {
      hasWeight = true;
    }

    if (!hasFill && lineRequiresHeliumPickup(line, weightHandleSet, fillHandleSet)) {
      hasFill = true;
    }

    if (hasFill && hasWeight) break;
  }

  return { hasFill, hasWeight };
}

/**
 * @returns {Promise<Record<string, unknown>>}
 */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cart: ${response.status}`);
  }

  return response.json();
}

/**
 * @param {{ hasFill: boolean, hasWeight: boolean }} flags
 * @returns {Promise<void>}
 */
async function writeAttributes(flags) {
  const response = await fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({
      attributes: {
        [FILL_ATTRIBUTE_KEY]: flags.hasFill ? 'true' : '',
        [WEIGHT_ATTRIBUTE_KEY]: flags.hasWeight ? 'true' : '',
      },
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update helium addon attributes: ${response.status}`);
  }
}

/**
 * @param {number} generation
 * @returns {Promise<void>}
 */
async function applySync(generation) {
  if (generation !== syncGeneration) return;

  const cart = await fetchCart();
  if (generation !== syncGeneration) return;

  const flags = cartAddonFlags(cart);
  const attributes = /** @type {Record<string, unknown> | undefined} */ (cart.attributes);
  const fillMatches = attributeIsOn(attributes?.[FILL_ATTRIBUTE_KEY]) === flags.hasFill;
  const weightMatches = attributeIsOn(attributes?.[WEIGHT_ATTRIBUTE_KEY]) === flags.hasWeight;

  if (fillMatches && weightMatches) return;
  if (generation !== syncGeneration) return;

  await writeAttributes(flags);
}

function scheduleSync() {
  const generation = ++syncGeneration;

  syncQueue = syncQueue
    .then(() => applySync(generation))
    .catch((error) => {
      console.error('[cart-helium-pickup-attribute]', error);
    });
}

/**
 * @param {Event} event
 */
function onCartLinesUpdate(event) {
  const promise = 'promise' in event ? event.promise : null;

  if (promise && typeof promise.then === 'function') {
    promise.then(
      () => scheduleSync(),
      () => {
        // Failed cart update left the lines unchanged, so the attribute stays as-is.
      }
    );
    return;
  }

  scheduleSync();
}

function boot() {
  const templateName = Theme.template?.name || '';
  if (templateName === 'password') return;

  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleSync();
  });

  scheduleSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
